import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { UserMenuContainerComponent } from './user-menu-container.component';
import { NotificationBellComponent } from './notification-bell.component';
import { HasAnyRoleDirective } from '../../shared/directives/has-any-role.directive';
import { HasAnyPermissionDirective } from '../../shared/directives/has-any-permission.directive';
import { Subscription } from 'rxjs';

@Component({
  selector: 'jhi-navbar',
  standalone: true,
  imports: [CommonModule, RouterModule, UserMenuContainerComponent, NotificationBellComponent, HasAnyRoleDirective, HasAnyPermissionDirective],
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.css']
})
export class NavbarComponent implements OnInit, OnDestroy {
  /**
   * Role được xem ít nhất một trang thống kê v2.
   * Toàn bộ là role nội bộ nên khách vãng lai (không có role nào) tự động không thấy menu.
   */
  private static readonly THONG_KE_ROLES = ['ROLE_ADMIN', 'ROLE_GIAM_DOC_NV', 'ROLE_NVKD',
    'ROLE_CHUYEN_VIEN_TD', 'ROLE_THAM_DINH_VIEN', 'ROLE_TDV_KY', 'ROLE_PHAN_CONG'];

  isNavbarCollapsed: boolean = true;
  innerWidth: number = 0;
  currentURl: string = '';
  QrStaff: string | null = null;
  currentUser: any = null;
  openMenu: 'thongke' | 'management' | 'configuration' | null = null;
  private userSubscription?: Subscription;
  /** Truyền cho chuông thông báo; giữ tham chiếu cố định để không tạo hàm mới mỗi lần change detection. */
  readonly kiemTraRole = (role: string): boolean => this.hasRole(role);

  constructor(
    private router: Router,
    private authService: AuthService
  ) {
    this.innerWidth = window.innerWidth;
    this.currentURl = this.router.url;
    this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        this.currentURl = event.url;
      }
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.innerWidth = window.innerWidth;
  }

  ngOnInit() {
    this.innerWidth = window.innerWidth;
    // Subscribe to current user for permission checking
    this.userSubscription = this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
    });
  }

  ngOnDestroy(): void {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
  }

  toggleNavbar() {
    this.isNavbarCollapsed = !this.isNavbarCollapsed;
    if (this.isNavbarCollapsed) this.openMenu = null;
  }

  collapseNavbar() {
    this.isNavbarCollapsed = true;
    this.openMenu = null;
  }

  @HostListener('document:click')
  closeMenusOnOutsideClick() { this.openMenu = null; }

  toggleMenu(event: MouseEvent, menu: 'thongke' | 'management' | 'configuration') {
    event.stopPropagation();
    this.openMenu = this.openMenu === menu ? null : menu;
  }

  closeMenuAndCollapse() { this.collapseNavbar(); }

  isThongKeRoute(): boolean {
    return this.currentURl.includes('/thong-ke') || this.currentURl.includes('/dashboard');
  }

  isManagementRoute(): boolean {
    return ['/quan-ly-nguoi-dung', '/danh-sach-user-tin-dung', '/quan-ly-nvkd'].some(path => this.currentURl.includes(path));
  }

  isConfigurationRoute(): boolean {
    return ['/auto-assignment', '/deadline-config'].some(path => this.currentURl.includes(path));
  }

  /**
   * Menu Thống kê hiện với các role nghiệp vụ được phân quyền xem báo cáo.
   * Khách vãng lai không có role nội bộ nào nên không thấy menu.
   *
   * Danh sách này phải khớp antMatchers trong SecurityConfiguration của backend
   * (document/thong_ke_bao_cao_v2_DESIGN.md mục 8.2).
   */
  hasThongKeAccess(): boolean {
    return !this.isPrintOnlyRole() && NavbarComponent.THONG_KE_ROLES.some(role => this.hasRole(role));
  }

  hasUserManagementAccess(): boolean {
    return !this.isPrintOnlyRole() && (this.hasRole('ROLE_ADMIN') || this.hasPermission('tdnh:list_own') || this.hasPermission('tdnh:manage_scope'));
  }

  hasConfigurationAccess(): boolean {
    return !this.isPrintOnlyRole() && (this.hasRole('ROLE_ADMIN') || this.hasRole('ROLE_GIAM_DOC_NV'));
  }

  checkMap() {
    this.currentURl = window.location.href;
    const checkCurrentUrl = this.currentURl.indexOf('map');
    if (checkCurrentUrl !== -1) {
      return false;
    }
    return true;
  }

  isAuthenticated() {
    return this.authService.isAuthenticated();
  }

  login() {
    this.router.navigate(['/login']);
  }

  routerPage(type: string) {
    this.router.navigate(['/', type]);
  }

  isThamDinhRoute(): boolean {
    // Check cả path và query params
    const url = this.currentURl.split('?')[0]; // Lấy path không có query params
    return url.includes('/quy-trinh-tham-dinh') || url.includes('/list');
  }

  hasThamDinhPermission(): boolean {
    return !this.isPrintOnlyRole() && (this.hasPermission('hstd:list_all') ||
      this.hasPermission('hstd:read_own') ||
      this.hasRole('ROLE_TDV_KY'));
  }

  /** ROLE_HANH_CHINH_IN_AN chỉ được hiển thị menu xử lý tài liệu in ấn. */
  isPrintOnlyRole(): boolean {
    return this.hasRole('ROLE_HANH_CHINH_IN_AN');
  }

  isHoSoPhanCongMode(mode: 'phan-cong' | 'tiep-nhan'): boolean {
    return this.currentURl.includes('/ho-so-phan-cong') && this.currentURl.includes(`mode=${mode}`);
  }

  hasPermission(permission: string): boolean {
    if (!this.currentUser) {
      // Check localStorage for permissions (if stored there)
      const permissionStr = localStorage.getItem('permissions');
      if (permissionStr) {
        try {
          const permissions = JSON.parse(permissionStr);
          if (Array.isArray(permissions)) {
            return permissions.some((perm: any) => {
              if (typeof perm === 'string') {
                return perm === permission || perm.includes(permission);
              }
              return perm?.permission === permission || perm?.permission?.includes(permission);
            });
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
      return false;
    }

    // Check permissions array
    if (this.currentUser.permissions && Array.isArray(this.currentUser.permissions)) {
      const hasPermission = this.currentUser.permissions.some((perm: any) => {
        if (typeof perm === 'string') {
          return perm === permission || perm.includes(permission);
        }
        return perm?.permission === permission || perm?.permission?.includes(permission);
      });
      if (hasPermission) return true;
    }

    // Check global_permissions array
    if (this.currentUser.global_permissions && Array.isArray(this.currentUser.global_permissions)) {
      const hasPermission = this.currentUser.global_permissions.some((perm: any) => {
        if (typeof perm === 'string') {
          return perm === permission || perm.includes(permission);
        }
        return perm?.permission === permission || perm?.permission?.includes(permission);
      });
      if (hasPermission) return true;
    }

    return false;
  }

  public hasRole(role: string): boolean {
    // Check localStorage 'role' (array of role strings)
    const roleStr = localStorage.getItem('role');
    if (roleStr) {
      try {
        const roles = JSON.parse(roleStr);
        if (Array.isArray(roles)) {
          if (roles.includes(role)) return true;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }

    // Check localStorage 'jhi-cn_role' (array of role objects with role property)
    const cnRoleStr = localStorage.getItem('jhi-cn_role');
    if (cnRoleStr) {
      try {
        const cnRoles = JSON.parse(cnRoleStr);
        if (Array.isArray(cnRoles)) {
          const hasRole = cnRoles.some((roleObj: any) => {
            const roleValue = roleObj?.role || roleObj?.name || roleObj?.code;
            return roleValue === role || roleValue?.includes(role);
          });
          if (hasRole) return true;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }

    // Check currentUser from AuthService
    if (this.currentUser) {
      // Check roles array
      if (this.currentUser.roles && Array.isArray(this.currentUser.roles)) {
        const hasRole = this.currentUser.roles.some((r: any) => {
          if (typeof r === 'string') {
            return r === role || r.includes(role);
          }
          const roleValue = r?.role || r?.name || r?.code;
          return roleValue === role || roleValue?.includes(role);
        });
        if (hasRole) return true;
      }

      // Check global_roles array
      if (this.currentUser.global_roles && Array.isArray(this.currentUser.global_roles)) {
        const hasRole = this.currentUser.global_roles.some((r: any) => {
          if (typeof r === 'string') {
            return r === role || r.includes(role);
          }
          const roleValue = r?.role || r?.name || r?.code;
          return roleValue === role || roleValue?.includes(role);
        });
        if (hasRole) return true;
      }
    }

    return false;
  }

}
